import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'

/**
 * The v2-native filter shape for dashboard-scoped filters: the dashboard's
 * global filter bar (`DashboardSettings.filters`) and a widget/text tile's
 * own filters (`DashboardItem.filterValues`). Replaces v1 `Filters` in both
 * roles — see `dashboards-v2-1/AGENTS.md`.
 *
 * Mirrors `ChartQueryPanel`'s `Pick<ChartPanel, ...>` pattern
 * (`charts-v2/types/chart-query-panel.ts`) rather than inventing a new
 * shape, so a chart tile's `ChartPanel` and a widget/global's
 * `DashboardFilterStateV2` share field names and can be merged/compared by
 * the same primitives (`merge-parent-filters-into-panel.ts`).
 */
export type DashboardFilterStateV2 = Pick<
  ChartPanel,
  'filters' | 'searchTerms' | 'searchCriteria' | 'smartSearchEnabled'
> & {
  /**
   * `null` = inherit the parent's date. At the dashboard level this is
   * never null (the dashboard is the top of the scope chain and always has
   * an active window). At the item level, `null` means "synced with the
   * dashboard" and a non-null value means "fixed" — the typed replacement
   * for v1's "absence/presence of a CREATE_TIME entry in `filterValues`"
   * convention.
   */
  dateRange: { startDate: number; endDate: number } | null
}

/** A dashboard-scoped filter state with no filters, search, or fixed date —
 *  the v2 analog of `emptyFilters` (`features/project/utils.ts`). Used to
 *  seed a widget/text tile's `filterValues` on creation (synced, no local
 *  overrides) and as the safe default when an item predates this shape. */
export const emptyDashboardFilterStateV2: DashboardFilterStateV2 = {
  filters: [],
  searchTerms: [],
  searchCriteria: {},
  dateRange: null,
}
